import type { AssetsShareCreateSchema } from '@iconik-io/iconik-api/src/services/assets';
import type { SettingsDefaultShareOptionsTypeSchema } from '@iconik-io/iconik-api/src/services/settings';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import {
  hasShareFieldRoles,
  type ShareFieldKey,
} from '../components/share_settings/utils';

export type DefaultShareOptions = SettingsDefaultShareOptionsTypeSchema & {
  allow_sync?: boolean;
  can_change_allow_sync?: boolean;
};

// `can_change_*` and `require_password` are share options but not fields on the
// created share, so they never reach sanitizeSettingByRoles.
type ShareSettingKey = keyof DefaultShareOptions & ShareFieldKey;

export const shareSettingsDefaults = {
  allow_approving_comments: true,
  allow_comments: true,
  allow_custom_actions: false,
  allow_download: true,
  allow_download_proxy: true,
  allow_setting_approve_status: true,
  allow_sync: true,
  allow_upload: false,
  allow_view_transcriptions: true,
  allow_view_versions: false,
  show_existing_comments: false,
  require_password: false,
  can_change_allow_upload: true,
  can_change_allow_download: true,
  can_change_allow_download_proxy: true,
  can_change_allow_custom_actions: true,
  can_change_allow_comments: true,
  can_change_allow_approving_comments: true,
  can_change_allow_setting_approve_status: true,
  can_change_allow_sync: true,
  can_change_allow_view_versions: true,
  can_change_search_users_from_share: true,
  can_change_share_expiration_time: true,
  can_change_allow_view_transcriptions: true,
  can_change_show_watermark: true,
  can_change_show_existing_comments: true,
  show_watermark: false,
};

export const addDefaultShareSettings = (
  data: Partial<DefaultShareOptions>,
  requestApproveMode: boolean,
  isSharingCollection: boolean,
  roles: UserRoles,
  settings?: SettingsDefaultShareOptionsTypeSchema | null,

  isSharingPlaylist?: boolean,
) => {
  const settingValues = !settings
    ? shareSettingsDefaults
    : { ...shareSettingsDefaults, ...settings };

  // Settings where user has insufficient roles will be set to false.
  const sanitizeSettingByRoles = (key: ShareSettingKey) => {
    if (!hasShareFieldRoles(key, roles)) {
      return false;
    }
    return data[key] !== undefined ? data[key] : settingValues[key];
  };

  const shareData = {
    allow_approving_comments: sanitizeSettingByRoles(
      'allow_approving_comments',
    ),
    allow_comments: sanitizeSettingByRoles('allow_comments'),
    allow_custom_actions: sanitizeSettingByRoles('allow_custom_actions'),
    allow_download: sanitizeSettingByRoles('allow_download'),
    allow_download_proxy: sanitizeSettingByRoles('allow_download_proxy'),
    allow_view_transcriptions: sanitizeSettingByRoles(
      'allow_view_transcriptions',
    ),
    allow_sync: sanitizeSettingByRoles('allow_sync'),
    allow_view_versions: sanitizeSettingByRoles('allow_view_versions'),
    show_watermark: sanitizeSettingByRoles('show_watermark'),
    ...(isSharingPlaylist && {
      show_existing_comments: sanitizeSettingByRoles('show_existing_comments'),
    }),
    // If we are creating a share for a review request this should always be true and the toggle hidden. For other shares we it should be false.
    allow_setting_approve_status:
      hasShareFieldRoles('allow_setting_approve_status', roles) &&
      requestApproveMode,
    ...(isSharingCollection && {
      allow_upload: sanitizeSettingByRoles('allow_upload'),
    }),
    review_experience_public_beta: true,
  } satisfies Partial<AssetsShareCreateSchema>;

  return shareData;
};
